import { Loader2, X } from "lucide-react";
import { useState } from "react";

import { AppHeader } from "@/components/app-header";
import { Card, CardContent } from "@/components/ui/card";
import { type ProposalItem, useApprovals } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

import { ProposalCard } from "./proposal-card";

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-accent-light text-primary",
  approved: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
  conflict: "bg-muted/15 text-foreground",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
        STATUS_STYLES[status] ?? STATUS_STYLES.conflict,
      )}
    >
      {status}
    </span>
  );
}

function outcomeMessage(result: ProposalItem): { tone: "ok" | "warn"; text: string } {
  switch (result.status) {
    case "approved":
      return { tone: "ok", text: `Approved and merged "${result.title}".` };
    case "rejected":
      return {
        tone: "ok",
        text: `Rejected "${result.title}". The proposer will see your note.`,
      };
    case "conflict":
      return {
        tone: "warn",
        text: `"${result.title}" changed since this proposal was made, so it could not be merged. The proposer needs to propose the change again.`,
      };
    default:
      return { tone: "ok", text: `"${result.title}" is now ${result.status}.` };
  }
}

export function ApprovalsPage() {
  const { data, isLoading, isError, refetch } = useApprovals();
  const [outcome, setOutcome] = useState<ProposalItem | null>(null);

  const pending = (data?.pending ?? []).filter((p) => p.can_decide);
  const recent = data?.recent ?? [];
  const message = outcome ? outcomeMessage(outcome) : null;

  return (
    <div className="bg-background min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-3xl space-y-8 px-6 py-10">
        <div>
          <h1 className="font-heading text-primary text-3xl font-bold">Approvals</h1>
          <p className="text-muted mt-2 text-sm">
            Proposed changes to wiki pages you own. Review the diff, then approve or reject.
          </p>
        </div>

        {message && (
          <div
            role="status"
            className={cn(
              "flex items-start justify-between gap-3 rounded-md border px-4 py-3 text-sm",
              message.tone === "ok"
                ? "border-success/40 bg-success/10"
                : "border-destructive/40 bg-destructive/10",
            )}
          >
            <p>{message.text}</p>
            <button
              type="button"
              onClick={() => setOutcome(null)}
              aria-label="Dismiss"
              className="text-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : isError || !data ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted text-sm">
                Could not load approvals.{" "}
                <button type="button" onClick={() => void refetch()} className="underline">
                  Try again
                </button>
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <section className="space-y-4">
              <h2 className="text-foreground text-lg font-semibold">
                Waiting for you ({pending.length})
              </h2>
              {pending.length === 0 ? (
                <Card>
                  <CardContent className="text-muted py-8 text-center text-sm">
                    Nothing waiting for your approval.
                  </CardContent>
                </Card>
              ) : (
                pending.map((proposal) => (
                  <ProposalCard key={proposal.id} proposal={proposal} onDecided={setOutcome} />
                ))
              )}
            </section>

            <section className="space-y-4">
              <h2 className="text-foreground text-lg font-semibold">Recent decisions</h2>
              {recent.length === 0 ? (
                <p className="text-muted text-sm">No decisions yet.</p>
              ) : (
                <Card>
                  <ul className="divide-border divide-y">
                    {recent.map((item) => (
                      <li key={item.id} className="space-y-1 px-5 py-4 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{item.title}</span>
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-muted text-xs">
                          Proposed by {item.proposer_name ?? item.proposer_email}
                          {item.decided_by && <> · decided by {item.decided_by}</>}
                          {item.decided_at && <> · {formatDateTime(item.decided_at)}</>}
                          {item.pr_url && (
                            <>
                              {" · "}
                              <a
                                href={item.pr_url}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-foreground underline"
                              >
                                PR{item.pr_number ? ` #${item.pr_number}` : ""}
                              </a>
                            </>
                          )}
                        </p>
                        {item.decision_note && (
                          <p className="text-foreground whitespace-pre-wrap">
                            &quot;{item.decision_note}&quot;
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
